import moment, { Moment } from "moment";
import { SlotInfo } from "react-big-calendar";

import DivisionServiceBlockDetail from "issara-sdk/apis/DivisionServiceBlockDetail_apps_QUE";
import DivisionServiceBlockNoTemplateBulkCreateView from "issara-sdk/apis/DivisionServiceBlockNoTemplateBulkCreateView_apps_QUE";
import DivisionServiceBlockNoTemplateBulkUpdateView from "issara-sdk/apis/DivisionServiceBlockNoTemplateBulkUpdateView_apps_QUEM";
import Scheduling from "issara-sdk/apis/Scheduling_apps_QUE";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { dateToSerial } from "react-lib/apps/Scheduling/common/Utils";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  ADD_PROVIDER: "ADD_PROVIDER",
  DELETE: "DELETE",
  EDIT: "EDIT",
  PICK_SLOT: "PICK_SLOT",
  PUBLISH: "PUBLISH",
  REMOVE_DRAFT: "REMOVE_DRAFT",
  SAVE: "SAVE",
  SEARCH: "SEARCH",
  UPDATE_DSB: "UPDATE_DSB",
} as const;

export const SEQUENCE_NAME = "ScheduleSetting" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  ScheduleSettingSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    events: ScheduleEventType[];
    filterSchedule: FilterScheduleType;
    weekStartDay: number; // 0=Sun, 1=Mon, ... 6=Sat
    year: number;
  }> | null;
}>;

export type PickedState = PickMainState<
  | "buttonLoadCheck"
  | "errorMessage"
  | "patientHNChatList"
  | "searchedItemListWithKey"
  | "selectedDivision"
  | "successMessage"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type ScheduleEventType = {
  id: number;
  date: string;
  end: Date;
  end_datetime: string;
  full_title: string;
  has_appointment: boolean;
  has_provider: boolean;
  is_closed: boolean;
  is_draft?: boolean;
  is_published: boolean;
  note?: string;
  providers: ProviderDetailType[];
  start: Date;
  start_datetime: string;
  time_slots: {
    end_time: string;
    items: Omit<ScheduleEventType, "time_slots">[];
    start_time: string;
  }[];
  title: string;
};

export type ScheduleInfoType = {
  date: Moment;
  end_time: string;
  ids?: number[];
  is_closed?: boolean;
  is_reschedule?: boolean;
  method?: AppointmentHandleMethodType;
  start_time: string;
};

export type AppointmentHandleMethodType =
  | "REMOVE_ALL"
  | "REMOVE_OVERFLOW"
  | "RESCHEDULE_AND_INSERT_OVERFLOW"
  | "RESCHEDULE_AND_REMOVE_OVERFLOW";

export type HolidayType = {
  date: Moment;
  name: string;
};

export type ProviderDetailType = {
  doctor_provider_id: number;
  dsb: DSBDetailType;
};

export type ProviderProfileType = ReturnType<typeof getProviderProfile>;

export type DSBDetailType = {
  appointments: {
    division_id?: number;
    edit_user_name: string | null;
    estimated_at: string;
    estimated_duration: number | null;
    first_name: string;
    hn: string;
    last_name: string;
    type_code: string;
  }[];
  date: string;
  division_id: number;
  division_name: string;
  doctor_dsb_end_time: string;
  doctor_dsb_id: number;
  doctor_dsb_slot_length: number;
  doctor_dsb_start_time: string;
  doctor_dsb_status: number;
  doctor_end_time: string;
  doctor_provider_first_name: string;
  doctor_provider_id: string;
  doctor_provider_last_name: string;
  doctor_provider_name: string;
  doctor_start_time: string;
  profile: ProviderProfileType;
  specialty: string;
  specialty_id_list: number[];
};

export type FilterScheduleType = Partial<{
  providers: { id: number; name?: string }[] | null;
  specialties: number[] | null;
  state: number;
}>;

export type AddProviderParamsType = {
  events: ScheduleEventType[];
  providers: Record<string, any>[];
  slot_length: number;
  onSuccess?: () => void;
};

export type UpdateDSBParamsType = {
  id: number;
  date?: string;
  deleted?: boolean;
  end_time?: string;
  inactive?: boolean;
  slot_length?: number;
  start_time?: string;
};

export type GroupedScheduleEventType = ScheduleEventType & {
  full_date: string;
  full_date_first_month_only: string;
  full_date_limited: string;
  full_date_without_year: string;
  items: ScheduleEventType[];
};

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = { division?: number };

export type SeqState = BaseSeqState<SequenceName> & { filter?: FilterScheduleType };

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.ADD_PROVIDER]: AddProviderParamsType;
  [ACTIONS.DELETE]: { data: ScheduleInfoType };
  [ACTIONS.EDIT]: { data: ScheduleInfoType };
  [ACTIONS.PICK_SLOT]: { selectedWeeks: string[]; slotInfo: SlotInfo };
  [ACTIONS.PUBLISH]: { btnAction?: string; data: number[]; onSuccess: () => void };
  [ACTIONS.SAVE]: {
    data: ScheduleInfoType;
    holidays: Record<string, any>[];
    selectedWeeks: string[];
  };
  [ACTIONS.SEARCH]: { year?: number };
  [ACTIONS.UPDATE_DSB]: UpdateDSBParamsType;
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  ScheduleSettingSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["specialty", {}]] as const;

const DATE_FORMAT = "YYYY-MM-DD";

export const METHOD_OPTIONS = [
  {
    label: "Reschedule overflow booking only",
    message: "Reschedule overflow booking only completed",
    value: "RESCHEDULE_AND_REMOVE_OVERFLOW",
  },
  {
    label: "Keep all appointments; insert overflow",
    message: "Keep all appointments; insert overflow completed",
    value: "RESCHEDULE_AND_INSERT_OVERFLOW",
  },
  {
    label: "Remove appointments outside new hours",
    message: "Remove appointments outside new hours completed",
    value: "REMOVE_OVERFLOW",
  },
  {
    label: "Reschedule all appointments",
    message: "Reschedule all appointments completed",
    value: "REMOVE_ALL",
  },
] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      ScheduleSettingSequence: {
        ...state.ScheduleSettingSequence,
        sequenceIndex: "Action",
        filterSchedule: params.filter ?? {},
        weekStartDay: 1,
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.ADD_PROVIDER]: HandleAddProvider,
    [ACTIONS.DELETE]: HandleDelete,
    [ACTIONS.EDIT]: HandleEdit,
    [ACTIONS.PICK_SLOT]: HandlePickSlot,
    [ACTIONS.PUBLISH]: HandlePublish,
    [ACTIONS.REMOVE_DRAFT]: HandleRemoveDraft,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.UPDATE_DSB]: HandleUpdateDSB,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);

  return () => {
    // ---
  };
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<unknown, Promise<void>> = async (controller) => {
  let state = controller.getState();

  const year = state.ScheduleSettingSequence?.year ?? moment().year();

  const dsbItems = await GetDSBFilter(controller);

  const formattedEvents = dsbItems.map((item) => {
    const formatted = formatScheduleEvent({
      date: moment(item.date),
      end_time: item.end_time,
      is_closed: item.status === 2,
      start_time: item.start_time,
    });

    const providers: ProviderDetailType[] = item.providers ?? [];
    const hasAppoint = providers.flatMap(
      (provider) => provider.dsb.appointments as Record<string, any>[]
    );
    const formattedProviders = providers.map((p) => {
      const data = p.dsb;

      const profile = getProviderProfile({
        first_name: data.doctor_provider_first_name,
        last_name: data.doctor_provider_last_name,
      });

      return { ...p, dsb: { ...p.dsb, profile } };
    });

    return {
      ...formatted,
      id: item.dsb_id,
      has_appointment: hasAppoint.length > 0,
      has_provider: providers.length > 0,
      is_published: item.state === 2,
      providers: formattedProviders,
    };
  });

  state = controller.getState();

  controller.setState({
    ScheduleSettingSequence: { ...state.ScheduleSettingSequence, events: formattedEvents, year },
  });
};

const HandlePickSlot: Handler<Params<Actions["PICK_SLOT"]>> = async (controller, params) => {
  const state = controller.getState();
  const { selectedWeeks, slotInfo } = params;
  const weekStartDay = state.ScheduleSettingSequence?.weekStartDay ?? 1;

  const data = {
    date: moment(slotInfo.start),
    end_time: moment(slotInfo.end).format("HH:mm"),
    is_draft: true,
    start_time: moment(slotInfo.start).format("HH:mm"),
  };

  const events = state.ScheduleSettingSequence?.events ?? [];
  const draftEvents = events.filter((event) => event.is_draft);

  if (draftEvents.length > 0) {
    HandleRemoveDraft(controller);

    return;
  }

  const updateEvents = generateEventsForSelectedWeeks(data, selectedWeeks, weekStartDay);

  controller.setState({
    ScheduleSettingSequence: {
      ...state.ScheduleSettingSequence,
      events: [...events, ...updateEvents],
    },
  });
};

const HandleRemoveDraft: Handler = async (controller) => {
  const state = controller.getState();

  const events = state.ScheduleSettingSequence?.events ?? [];

  const filtered = events.filter((event) => !event.is_draft);

  controller.setState({
    ScheduleSettingSequence: { ...state.ScheduleSettingSequence, events: filtered },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  const state = controller.getState();
  const { data, holidays, selectedWeeks } = params;
  const holidayDates = holidays.map((holiday) => moment(holiday.dates[0]));
  const weekStartDay = state.ScheduleSettingSequence?.weekStartDay ?? 1;

  let updateEvents: ScheduleEventType[] = [];

  if (selectedWeeks.length === 1) {
    const formattedEvent = formatScheduleEvent(data);

    updateEvents = [formattedEvent];
  } else {
    updateEvents = generateEventsForSelectedWeeks(data, selectedWeeks, weekStartDay);
  }

  // กรองเฉพาะ events ที่ไม่ตรงกับวันหยุดและไม่เป็นวันในอดีต
  const filteredUpdateEvents = updateEvents.filter((event) => {
    const eventDate = moment(event.start);

    const isHoliday = holidayDates.some((holidayDate) => eventDate.isSame(holidayDate, "day"));
    const isPastDate = eventDate.isBefore(moment());

    return !isHoliday && !isPastDate;
  });

  const [, error] = await CreateDSBNoTemplate(controller, { events: filteredUpdateEvents });

  if (error) {
    HandleRemoveDraft(controller);
    SetErrorMessage(controller, { ...params, btnError: null, error });

    return;
  }

  await HandleSearch(controller);
};

const HandleDelete: Handler<Params<Actions["DELETE"]>> = async (controller, params) => {
  const [, error] = await UpdateDSBNoTemplate(controller, {
    action: "DELETE",
    ids: params.data.ids,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller);
};

const HandlePublish: Handler<Params<Actions["PUBLISH"]>> = async (controller, params) => {
  const state = controller.getState();

  if (params.btnAction) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [params.btnAction]: BLC_STATUS.LOADING },
    });
  }

  const [, error] = await UpdateDSBNoTemplate(controller, { action: "PUBLISH", ids: params.data });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller);

  if (params.btnAction) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [params.btnAction]: BLC_STATUS.SUCCESS },
    });
  }

  params.onSuccess();
};

const HandleEdit: Handler<Params<Actions["EDIT"]>> = async (controller, params) => {
  const state = controller.getState();
  const events = state.ScheduleSettingSequence?.events ?? [];

  const ids = params.data.ids ?? [];
  const now = moment();
  const filteredEvents = events
    .filter((event) => ids.includes(event.id))
    .map((event) => {
      const date = moment(event.start_datetime);
      const formattedEvent = formatScheduleEvent({ ...params.data, date });

      return {
        id: event.id,
        end_datetime: formattedEvent.end_datetime,
        start_datetime: formattedEvent.start_datetime,
      };
    })
    .filter((event) => moment(event.start_datetime).isSameOrAfter(now));

  const [, error] = await DivisionServiceBlockNoTemplateBulkUpdateView.put({
    apiToken: controller.apiToken,
    data: {
      action: params.data.is_closed ? "CLOSE" : "EDIT_SCHEDULE",
      appointment_handle_method: params.data.method,
      items: filteredEvents,
    },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller);

  if (params.data.method) {
    const foundMethod = METHOD_OPTIONS.find((option) => option.value === params.data.method);

    controller.setState({
      successMessage: { ...state.successMessage, [params.sequence]: foundMethod?.message || "" },
    });
  }
};

const HandleAddProvider: Handler<Params<Actions["ADD_PROVIDER"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_PROVIDER]: BLC_STATUS.LOADING },
  });

  const [, error] = await CreateDSBNoTemplate(controller, params);

  if (error) {
    HandleRemoveDraft(controller);
    SetErrorMessage(controller, {
      ...params,
      btnError: null,
      error,
    });

    return;
  }

  await HandleSearch(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_PROVIDER]: null },
  });

  params.onSuccess?.();
};

const HandleUpdateDSB: Handler<Params<Actions["UPDATE_DSB"]>> = async (controller, params) => {
  const startDatetime = `${params.date} ${params.start_time}`;
  const endDatetime = `${params.date} ${params.end_time}`;

  const data = {
    end_serial: dateToSerial(endDatetime),
    slot_length: params.slot_length,
    start_serial: dateToSerial(startDatetime),
    status: params.inactive ? 2 : 3,
  };

  const api = params.deleted ? DivisionServiceBlockDetail.delete : DivisionServiceBlockDetail.patch;

  const [, error] = await api({
    apiToken: controller.apiToken,
    data: data as any,
    pk: params.id,
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller);
};

/** ====================================================  */
/**                        // APIs                        */
/** ====================================================  */
const GetDSBFilter: Handler<unknown, Promise<Record<string, any>[]>> = async (controller) => {
  const state = controller.getState();

  const seq = state.ScheduleSettingSequence;
  const year = seq?.year ?? moment().year();
  const weekStartDay = seq?.weekStartDay ?? 1;
  const { firstWeek, lastWeek } = getYearWeekRange(year, weekStartDay);

  // จำกัดให้ firstWeek และ lastWeek ไม่ข้ามปี
  const yearStart = moment(`${year}-01-01`).format(DATE_FORMAT);
  const yearEnd = moment(`${year}-12-31`).format(DATE_FORMAT);

  const boundedFirstWeek = moment.max(moment(firstWeek), moment(yearStart)).format(DATE_FORMAT);
  const boundedLastWeek = moment
    .min(moment(lastWeek).add(6, "days"), moment(yearEnd))
    .format(DATE_FORMAT);

  const filterProviders = seq?.filterSchedule?.providers ?? [];
  const filterSpecialties = seq?.filterSchedule?.specialties ?? [];
  const hasFilter = filterProviders.length > 0 || filterSpecialties.length > 0;
  const allProviderIds: number[] = [];

  if (hasFilter) {
    const [result] = await Scheduling.get({
      apiToken: controller.apiToken,
      command: "ProviderSearch",
      params: {
        division: controller.data.division,
        specialties: filterSpecialties,
      },
    });

    const allProviders = [
      ...filterProviders,
      ...(result as any[]).map((provider: any) => ({
        id: Number(provider.provider_id),
      })),
    ];

    allProviderIds.push(...new Set(allProviders.map((provider) => provider.id)));
  }

  const requestParam = hasFilter
    ? {
        command: "DSBProviderFilter",
        provider_ids: allProviderIds,
      }
    : { command: "DivisionServiceBlockFilter", divisions: [controller.data.division] };

  const [response] = await Scheduling.get({
    apiToken: controller.apiToken,
    command: requestParam.command,
    params: {
      ...requestParam,
      end_date: boundedLastWeek,
      start_date: boundedFirstWeek,
      state: seq?.filterSchedule?.state,
    },
  });

  let dsbItems = (response ?? []) as Record<string, any>[];

  dsbItems = dsbItems.map((item) => {
    const providers = item.providers as ProviderDetailType[];

    return {
      ...item,
      providers: providers.map((provider) => ({
        ...provider,
        dsb: {
          ...provider.dsb,
          doctor_dsb_end_time: item.end_time,
          doctor_dsb_start_time: item.start_time,
        },
      })),
    };
  });

  if (hasFilter) {
    // ถ้ามีการ  filter ให้ใช้เวลาของ Provider
    dsbItems = dsbItems.flatMap((item) => {
      const providers = item.providers as ProviderDetailType[];

      return providers.map((provider) => ({
        ...item,
        doctor_dsb_id: provider.dsb.doctor_dsb_id,
        end_time: provider.dsb.doctor_end_time,
        providers: [provider],
        start_time: provider.dsb.doctor_start_time,
      }));
    });

    return [...new Map(dsbItems.map((item) => [item.doctor_dsb_id, item])).values()];
  }

  return dsbItems;
};

const CreateDSBNoTemplate: Handler<
  { events: ScheduleEventType[]; providers?: Record<string, any>[]; slot_length?: number },
  Promise<[unknown, unknown]>
> = async (controller, params) => {
  const formattedEvents = params.events.flatMap((event) => {
    const baseEvent = {
      end_datetime: event.end_datetime,
      start_datetime: event.start_datetime,
      ...(params.slot_length && {
        slot_length: params.slot_length,
      }),
    };

    if (params.providers && params.providers.length > 0) {
      return params.providers.map((provider) => ({
        ...baseEvent,
        provider: provider.provider_id,
      }));
    }

    return [baseEvent];
  });

  const [res, error] = await DivisionServiceBlockNoTemplateBulkCreateView.post({
    apiToken: controller.apiToken,
    data: {
      division: controller.data.division,
      items: formattedEvents,
    },
  });

  const errList = res?.error_list ?? [];
  const dupList: Record<string, any>[] = res?.duplicated_list ?? [];
  const formattedDupList = dupList.map((item) => {
    const foundProvider = (params.providers ?? []).find(
      (provider) => provider.provider_id === item.provider
    );
    const profile = getProviderProfile(foundProvider ?? {});

    return { ...item, provider_name: profile.full_name.trim() };
  });
  const errorResult = errList.length > 0 ? errList : error;

  return [res, dupList.length > 0 ? formattedDupList : errorResult];
};

const UpdateDSBNoTemplate: Handler<
  { action: "DELETE" | "PUBLISH"; ids?: number[] },
  Promise<[unknown, unknown]>
> = async (controller, params) => {
  const data = {
    action: params.action,
    items: (params.ids ?? []).map((id) => ({ id })),
  };

  const api =
    params.action === "DELETE"
      ? DivisionServiceBlockNoTemplateBulkUpdateView.delete
      : DivisionServiceBlockNoTemplateBulkUpdateView.put;

  return api({
    apiToken: controller.apiToken,
    data,
  }) as Promise<[unknown, unknown]>;
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */

const formatScheduleEvent = (data: {
  date: Moment;
  end_time: string;
  is_closed?: boolean;
  is_draft?: boolean;
  start_time: string;
}): ScheduleEventType => {
  const [startHour, startMin] = data.start_time.split(":").map(Number);
  const [endHour, endMin] = data.end_time.split(":").map(Number);

  const start = data.date.clone().hour(startHour).minute(startMin).second(0);
  const end = data.date.clone().hour(endHour).minute(endMin).second(0);

  const formatTime = (hour: number, minute: number) => {
    const period = hour < 12 ? "AM" : "PM";
    const displayHour = hour % 12 || 12;
    const minuteText = minute > 0 ? `:${minute.toString().padStart(2, "0")}` : "";

    return `${displayHour}${minuteText} ${period}`;
  };

  const formatFullTime = (hour: number, minute: number) =>
    `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;

  const timeText = `${formatTime(startHour, startMin)} - ${formatTime(endHour, endMin)}`;
  const fullTimeText = `${formatFullTime(startHour, startMin)} - ${formatFullTime(
    endHour,
    endMin
  )}`;

  return {
    id: Date.now(),
    date: data.date.format("MMM.D"),
    end: end.toDate(),
    end_datetime: end.format(),
    full_title: fullTimeText,
    has_appointment: false,
    has_provider: false,
    is_closed: data.is_closed ?? false,
    is_draft: data.is_draft ?? false,
    is_published: false,
    providers: [],
    start: start.toDate(),
    start_datetime: start.format(),
    time_slots: [
      {
        end_time: formatFullTime(endHour, endMin),
        items: [],
        start_time: formatFullTime(startHour, startMin),
      },
    ],
    title: timeText,
  };
};

const convertDateToWeek = (
  originalDate: Moment,
  weekStartMoment: Moment,
  weekStartDay = 1
): Moment => {
  const weekStart = weekStartMoment.clone().isoWeekday(weekStartDay);
  const dayOfWeek = originalDate.isoWeekday() - weekStartDay;

  return weekStart
    .clone()
    .add(dayOfWeek, "days")
    .hour(originalDate.hour())
    .minute(originalDate.minute())
    .second(originalDate.second());
};

export const formatEventsForWeekView = (
  events: ScheduleEventType[],
  weekStartMoment: Moment,
  weekStartDay = 1
): ScheduleEventType[] =>
  events.map((event) => {
    const originalStart = moment(event.start);
    const originalEnd = moment(event.end);

    const updateStart = convertDateToWeek(originalStart, weekStartMoment, weekStartDay);
    const updateEnd = convertDateToWeek(originalEnd, weekStartMoment, weekStartDay);

    return {
      ...event,
      end: updateEnd.toDate(),
      start: updateStart.toDate(),
    };
  });

export const formatHolidaysForWeekView = (
  holidays: HolidayType[],
  weekStartMoment: Moment,
  weekStartDay = 1
): HolidayType[] =>
  holidays.map((holiday) => {
    const updatedDate = convertDateToWeek(holiday.date, weekStartMoment, weekStartDay);

    return {
      ...holiday,
      date: updatedDate,
    };
  });

// Group events ที่มีวันและเวลาเดียวกัน
const getFullDateString = (
  byMonth: Record<string, string[]>,
  dayOfWeek: string,
  includeYear: boolean
): string =>
  Object.entries(byMonth)
    .map(([monthYear, days]) => {
      const [month, year] = monthYear.split(" ");
      const dateStr = `${dayOfWeek}   ${days.join(",")} ${month}.`;

      return includeYear ? `${dateStr} ${year}` : dateStr;
    })
    .join(" | ");

// format วันที่แบบแสดงวันเฉพาะเดือนแรก
const formatFirstMonthOnly = (byMonth: Record<string, string[]>, dayOfWeek: string): string =>
  Object.entries(byMonth)
    .map(([monthYear, days], index) => {
      const [month] = monthYear.split(" ");

      return index === 0
        ? `${dayOfWeek} ${days.join(",")} ${month}.`
        : `${days.join(",")} ${month}`;
    })
    .join(" | ");

export const generateGroupedDateStrings = (sortedEvents: ScheduleEventType[]) => {
  const now = moment();
  const firstMoment = moment(sortedEvents[0].start_datetime);
  const dayOfWeek = firstMoment.format("ddd");
  const byMonth: Record<string, string[]> = {};
  const byMonthWithoutPast: Record<string, string[]> = {};

  for (const e of sortedEvents) {
    const m = moment(e.start_datetime);
    const monthKey = m.format("MMM YYYY");

    // Add to byMonth (all dates)
    byMonth[monthKey] ??= [];
    byMonth[monthKey].push(m.format("D"));

    if (!m.isBefore(now)) {
      byMonthWithoutPast[monthKey] ??= [];
      byMonthWithoutPast[monthKey].push(m.format("D"));
    }
  }

  // Create limited version (max 2 months)
  const byMonthLimited: Record<string, string[]> = {};
  const monthKeys = Object.keys(byMonth);

  let hasMore = false;

  if (monthKeys.length > 2) {
    hasMore = true;

    for (const key of monthKeys.slice(0, 2)) {
      byMonthLimited[key] = byMonth[key];
    }
  } else {
    Object.assign(byMonthLimited, byMonth);
  }

  const fullDateLimited = formatFirstMonthOnly(byMonthLimited, dayOfWeek) + (hasMore ? "..." : "");

  const fullDateFirstMonthOnly = formatFirstMonthOnly(byMonth, dayOfWeek);
  const fullDateFirstMonthOnlyWithoutPast = formatFirstMonthOnly(byMonthWithoutPast, dayOfWeek);

  return {
    full_date: getFullDateString(byMonth, dayOfWeek, true),
    full_date_first_month_only: fullDateFirstMonthOnly,
    full_date_first_month_only_without_past: fullDateFirstMonthOnlyWithoutPast,
    full_date_limited: fullDateLimited,
    full_date_without_past_date: getFullDateString(byMonthWithoutPast, dayOfWeek, false),
    full_date_without_past_date_with_year: getFullDateString(byMonthWithoutPast, dayOfWeek, true),
    full_date_without_year: getFullDateString(byMonth, dayOfWeek, false),
  };
};

export const groupEventsByDayAndTime = (
  events: ScheduleEventType[],
  hasFilter = false,
  weekStartDay = 1
): GroupedScheduleEventType[] => {
  // Group events by day of week and time
  const groupedMap = new Map<string, ScheduleEventType[]>();

  for (const event of events) {
    const start = moment(event.start);
    const end = moment(event.end);
    const providerId = hasFilter ? event.providers[0].doctor_provider_id : "";

    const dayOfWeek = start.isoWeekday() - weekStartDay;

    const key = `${dayOfWeek}-${start.format("HH:mm")}-${end.format("HH:mm")}-${event.is_draft}-${
      event.is_closed
    }-${providerId}-${event.has_provider}`;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, []);
    }

    groupedMap.get(key)?.push(event);
  }

  // Convert grouped events to result format
  const result: GroupedScheduleEventType[] = [];

  for (const groupEvents of groupedMap.values()) {
    if (groupEvents.length === 0) {
      continue;
    }

    const sortedEvents = [...groupEvents].sort(
      (a, b) => moment(a.start_datetime).valueOf() - moment(b.start_datetime).valueOf()
    );

    const baseEvent = sortedEvents[0];

    // Create date string
    const dates = sortedEvents.map((e) => moment(e.start_datetime).format("MMM D"));
    const dateString = dates.join(", ");

    const dateStrings = generateGroupedDateStrings(sortedEvents);

    result.push({
      ...baseEvent,
      date: dateString,
      ...dateStrings,
      items: sortedEvents,
    });
  }

  return result;
};

export const getSelectedDateRanges = (selectedWeeks: string[], weekStartDay = 1) =>
  selectedWeeks.map((weekValue) => {
    const weekStart = moment(weekValue, DATE_FORMAT).isoWeekday(weekStartDay);
    const weekEnd = weekStart.clone().add(6, "days");

    return { end: weekEnd, start: weekStart };
  });

export const isDateInSelectedRanges = (
  date: Moment,
  dateRanges: { end: Moment; start: Moment }[]
) => dateRanges.some((range) => date.isBetween(range.start, range.end, "day", "[]"));

export const getYearWeekRange = (year: number, weekStartDay = 1) => {
  const startOfYear = moment().year(year).startOf("year");
  const endOfYear = moment().year(year).endOf("year");

  // หาวันแรกของสัปดาห์แรก
  const firstWeek = startOfYear.clone().isoWeekday(weekStartDay);

  if (firstWeek.isAfter(startOfYear)) {
    firstWeek.subtract(1, "week");
  }

  // หาวันแรกของสัปดาห์สุดท้าย
  const lastWeek = endOfYear.clone().isoWeekday(weekStartDay);

  if (lastWeek.isAfter(endOfYear)) {
    lastWeek.subtract(1, "week");
  }

  return {
    firstWeek: firstWeek.format(DATE_FORMAT),
    lastWeek: lastWeek.format(DATE_FORMAT),
  };
};

export const convertStringToColor = (string: string) => {
  // Use 32-bit integer operations to prevent overflow
  const MAX_32BIT = 0xffffffff;

  let hash1 = 0;
  let hash2 = 0;
  let hash3 = 0;

  for (let index = 0; index < string.length; index++) {
    const codePoint = string.codePointAt(index) ?? 0;
    const position = index + 1;

    // Hash 1: Keep within 32-bit range
    hash1 = (codePoint * position + ((hash1 << 5) - hash1)) & MAX_32BIT;

    // Hash 2: Use modulo to prevent overflow
    hash2 = (codePoint + hash2 * 37 + position * position) % MAX_32BIT >>> 0;

    // Hash 3: XOR-based (already safe)
    hash3 ^= (codePoint << index % 16) + position * 13;
    hash3 >>>= 0; // Keep unsigned 32-bit
  }

  // Combine hashes with modulo to prevent overflow
  const combinedHash = Math.abs((hash1 + hash2 * 7 + hash3 * 13) % MAX_32BIT);

  // Use Fibonacci hashing for better distribution
  const fibHash = (combinedHash * 2654435769) >>> 0;

  // Map to hue with better distribution (0-360)
  const hue = (fibHash * 137.508) % 360;

  // Create variation based on string characteristics
  const lengthFactor = string.length % 10;
  const hashFactor = (fibHash >> 8) % 20;

  // UPDATED: Stronger pastel colors
  // Saturation: 50-70% (much higher for vibrant pastels)
  // Lightness: 75-85% (lower for more visible colors)
  const saturation = 50 + hashFactor + (lengthFactor % 10);
  const lightness = 75 + ((fibHash >> 16) % 11);

  // console.log("convertStringToColor from", string, "colorMap:", colorMap);

  return {
    // Dark variant: slightly darker and more saturated
    dark: `hsl(${Math.round(hue)}, ${Math.min(saturation + 10, 75)}%, ${Math.max(
      lightness - 8,
      68
    )}%)`,

    // Light variant: lighter and slightly less saturated
    light: `hsl(${Math.round(hue)}, ${Math.max(saturation - 10, 45)}%, ${Math.min(
      lightness + 8,
      90
    )}%)`,

    // Medium variant: base color
    medium: `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`,

    // Text colors with better contrast
    textDark: `hsl(${Math.round(hue)}, ${Math.min(saturation + 20, 70)}%, 10%)`,
    textLight: "#FFFFFF",
  };
};

const getFirstChar = (text: string) => {
  // หาพยัชนะไทยตัวแรกในข้อความ (ก-ฮ)
  // ถ้าไม่เจอ ให้ใช้ตัวอักษรแรกแล้วแปลงเป็นตัวพิมพ์ใหญ่
  const match = /[\u0E01-\u0E2E]/.exec(text);

  return match ? match[0] : text.charAt(0).toUpperCase();
};

export const getProviderProfile = (data: Record<string, any>) => {
  const firstName = data.first_name || "";
  const lastName = data.last_name || "";
  const fullName = `${data.pre_name || ""} ${firstName} ${lastName}`;
  const color = convertStringToColor(fullName);

  return {
    abbr: `${getFirstChar(firstName)}${getFirstChar(lastName)}`,
    code: data.code,
    color,
    first_name: data.first_name,
    full_name: fullName,
    last_name: data.last_name,
    pre_name: data.pre_name,
    provider_id: data.provider_id,
  };
};

export const calculateSlotInfo = (data: Record<string, any>) => {
  const startTime: string = data.doctor_start_time || data.start_time;
  const endTime: string = data.doctor_end_time || data.end_time;
  const slotLength = data.doctor_dsb_slot_length;

  // แปลงเวลาเป็นนาที
  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);

  const startHourInMinutes = startHour * 60;
  const startMinutes = startHourInMinutes + startMinute;

  const endHourInMinutes = endHour * 60;
  const endMinutes = endHourInMinutes + endMinute;

  const totalMinutes = endMinutes - startMinutes;
  const totalSlots = slotLength > 0 ? Math.floor(totalMinutes / slotLength) : 0;

  const scheduledCount: number = data.appointments?.length || 0;
  const isFull = scheduledCount >= totalSlots;

  return {
    isFull,
    scheduledCount,
    statusText: isFull ? "FULL" : `${scheduledCount} / ${totalSlots}`,
    totalSlots,
  };
};

export const aggregateSlotInfo = (providers: ProviderDetailType[]) => {
  const slotInfoList = providers.map((provider) => calculateSlotInfo(provider.dsb));

  const totalScheduled = slotInfoList.reduce((sum, info) => sum + info.scheduledCount, 0);
  const totalSlots = slotInfoList.reduce((sum, info) => sum + info.totalSlots, 0);
  const isFull = totalScheduled >= totalSlots;

  return {
    isFull,
    providers: slotInfoList,
    scheduledCount: totalScheduled,
    statusText: isFull ? "FULL" : `${totalScheduled} / ${totalSlots}`,
    totalSlots,
  };
};

const matchesProviderOrSpecialty = (
  provider: ScheduleEventType["providers"][number],
  providerIds?: number[] | null,
  specialtyIds?: number[] | null
) => {
  const hasProvider = !!providerIds?.includes(provider.doctor_provider_id);
  const hasSpecialty = !!specialtyIds?.some((id) => provider.dsb.specialty_id_list.includes(id));

  return hasProvider || hasSpecialty;
};

export const findMatchingProvider = (
  items: ScheduleEventType[],
  providerIds?: number[] | null,
  specialtyIds?: number[] | null
) =>
  items
    .flatMap((event) => event.providers)
    .find((provider) => matchesProviderOrSpecialty(provider, providerIds, specialtyIds));

export const filterMatchingProviders = (
  items: ScheduleEventType[],
  providerIds?: number[] | null,
  specialtyIds?: number[] | null
) =>
  items
    .flatMap((event) => event.providers)
    .filter((provider) => matchesProviderOrSpecialty(provider, providerIds, specialtyIds));

export const timeToDecimal = (timeStr?: string) => {
  if (!timeStr) {
    return 0;
  }

  const [hours, minutes] = timeStr.split(":");

  return Number.parseInt(hours) + Number.parseInt(minutes) / 60;
};

export const generateEventsForSelectedWeeks = (
  data: {
    date: moment.Moment;
    end_time: string;
    is_draft?: boolean;
    start_time: string;
  },
  selectedWeeks: string[],
  weekStartDay = 1
) => {
  const originalDayOfWeek = data.date.isoWeekday();

  return selectedWeeks.map((weekStartDate) => {
    const weekStart = moment(weekStartDate).isoWeekday(weekStartDay);
    const dayOffset = originalDayOfWeek - weekStartDay;
    const targetDate = weekStart.clone().add(dayOffset, "days");

    return formatScheduleEvent({
      ...data,
      date: targetDate,
    });
  });
};

export const getFormattedEventItems = (
  eventItems: GroupedScheduleEventType["items"],
  eventTime: { end: Date; start: Date }
) => {
  const startTime = moment(eventTime.start).format("HH:mm");
  const endTime = moment(eventTime.end).format("HH:mm");

  return eventItems.map((item) => {
    const date = moment(item.start_datetime).format("YYYY-MM-DD");

    return {
      ...item,
      end_datetime: moment(`${date} ${endTime}`).format(),
      start_datetime: moment(`${date} ${startTime}`).format(),
    };
  });
};
